/**
 * Default seed library expressions, installed once when a user first opens
 * the library and it's empty for that namespace. Mirrors the legacy
 * "High Chg" seed.
 *
 * Column ids referenced here must exist in the catalog at install time —
 * the installer skips any seed whose columns are missing rather than
 * persisting a broken expression.
 */

import {
  generateLibraryExpressionId,
  type ExpressionFormat,
  type ExpressionNamespace,
  type ExpressionSymbol,
  type LibraryExpression,
} from '@/features/sliced-form/schemas/expression'

export type SeedTemplate = {
  label: string
  formula: string
  args: Record<string, { column: string }>
  format: ExpressionFormat
  symbol: ExpressionSymbol
}

/**
 * Legacy "High Chg" seed (per `expressions/initialState.js` audit). Uses
 * intraday-high vs premarket-high vs open as a stand-in — `o` (open) is
 * substituted for `day_m1_close` because the new realtime catalog doesn't
 * carry a standalone previous-close column.
 */
export const REALTIME_SEEDS: ReadonlyArray<SeedTemplate> = [
  {
    label: 'High Chg',
    formula: 'GREATEST([[A]], [[B]]) - [[C]]',
    args: {
      A: { column: 'h' },
      B: { column: 'pm_h' },
      C: { column: 'day_m1_close' },
    },
    format: 'fixed',
    symbol: 'currency',
  },
]

/**
 * Historical equivalent — uses day0 columns. Same shape as the realtime
 * seed so users see continuity across namespaces.
 */
export const HISTORICAL_SEEDS: ReadonlyArray<SeedTemplate> = [
  {
    label: 'High Chg',
    formula: 'GREATEST([[A]], [[B]]) - [[C]]',
    args: {
      A: { column: 'day0_high' },
      B: { column: 'day0_pm_high' },
      C: { column: 'day_m1_close' },
    },
    format: 'fixed',
    symbol: 'currency',
  },
]

/**
 * Build the seed list for a namespace, dropping seeds whose columns are
 * missing from the catalog. Caller passes a column-existence predicate
 * (typically `(id) => id in COLUMN_MAP`).
 */
export function buildLibrarySeedsForNamespace(
  namespace: ExpressionNamespace,
  columnExists: (id: string) => boolean,
  now: () => string = () => new Date().toISOString(),
): LibraryExpression[] {
  const templates = namespace === 'realtime' ? REALTIME_SEEDS : HISTORICAL_SEEDS
  const ts = now()
  const out: LibraryExpression[] = []
  for (const tpl of templates) {
    const allColumnsExist = Object.values(tpl.args).every((a) => columnExists(a.column))
    if (!allColumnsExist) continue
    out.push({
      id: generateLibraryExpressionId(),
      namespace,
      label: tpl.label,
      formula: tpl.formula,
      args: tpl.args,
      format: tpl.format,
      symbol: tpl.symbol,
      createdAt: ts,
      updatedAt: ts,
    })
  }
  return out
}
